import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { FALLBACK_ERROR_MESSAGE } from "@/lib/constants";
import { registerSchema, type RegisterFormData } from "@/lib/validations";
import { zodResolver } from "@hookform/resolvers/zod";
import { Github, Loader2, Terminal } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Navigate } from "react-router-dom";

type DashboardLocationState = {
  signup?: boolean;
};

export function AuthPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [isLoading, setLoading] = useState(false);
  const [redirectState, setRedirectState] = useState<
    DashboardLocationState | undefined
  >(undefined);
  const { signUp, signIn, user } = useAuth();
  const { toast } = useToast();

  const {
    register: registerField,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  if (user && !isLoading) {
    return <Navigate to="/dashboard" replace state={redirectState} />;
  }

  const handleLogin = async () => {
    try {
      setLoading(true);
      setRedirectState(undefined);
      const result = await signIn();
      if (!result.success) {
        throw new Error(result.error ?? FALLBACK_ERROR_MESSAGE);
      }
    } catch (error) {
      let message = FALLBACK_ERROR_MESSAGE;
      //@ts-expect-error todo
      if (error?.code === "auth/invalid-credential") {
        message = "Invalid Credentials";
      }
      toast({
        variant: "destructive",
        title: message,
      });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async () => {
    try {
      setLoading(true);

      const result = await signUp();
      if (!result.success) {
        throw new Error(result.error ?? FALLBACK_ERROR_MESSAGE);
      }
      toast({
        title: "Sign up successful",
      });
      setRedirectState({ signup: Boolean(result.signup) });
    } catch (error) {
      let errorMessage = FALLBACK_ERROR_MESSAGE;
      //@ts-expect-error todo
      if (error?.code === "auth/email-already-in-use") {
        errorMessage = "Account already exists, please login instead";
      }
      toast({
        variant: "destructive",
        title: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] cyber-grid p-4 sm:p-6 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 sm:w-96 sm:h-96 bg-[var(--primary)] rounded-full blur-[100px] opacity-10 sm:opacity-20"></div>
      </div>

      <Card className="w-full max-w-md holo-card relative z-10 py-6 sm:py-7 border-[var(--primary)]/30">
        <CardHeader className="text-center space-y-3 sm:space-y-4 px-4 sm:px-6">
          <div className="flex justify-center mb-2">
            <div className="p-3 bg-[var(--primary)]/10 rounded-full border border-[var(--primary)]/30">
              <Terminal className="w-8 h-8 text-[var(--primary)]" />
            </div>
          </div>
          <div>
            <CardTitle className="text-2xl sm:text-3xl font-bold font-mono tracking-tight text-[var(--text-main)]">
              VIBE DEPLOY
            </CardTitle>
          </div>
          <CardDescription className="text-[var(--text-subtle)] text-sm sm:text-base font-mono">
            {isLogin ? "AUTHENTICATION_REQUIRED" : "INITIALIZE_NEW_USER"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5 sm:space-y-6 px-4 sm:px-6">
          <Button
            onClick={handleLogin}
            disabled={isLoading}
            className="w-full cyber-button h-12 sm:h-12 text-sm sm:text-base group"
            size="lg"
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 mr-2 sm:mr-3 animate-spin" />
            ) : (
              <Github className="h-5 w-5 mr-2 sm:mr-3 group-hover:text-[var(--primary)]" />
            )}
            {isLoading ? "CONNECTING..." : "CONNECT WITH GITHUB"}
          </Button>

          {!isLogin && (
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-3 sm:space-y-4"
            >
              <div className="space-y-1.5 sm:space-y-2">
                <Label
                  htmlFor="name"
                  className="text-[var(--primary)] text-sm sm:text-base font-mono"
                >
                  FULL_NAME
                </Label>
                <Input
                  id="name"
                  type="text"
                  {...registerField("name")}
                  className="bg-[var(--bg-alt)] border-[var(--border-color)] text-[var(--text-main)] focus:border-[var(--primary)] h-11 sm:h-10 font-mono"
                />
                {errors.name && (
                  <p className="text-[var(--error)] text-xs font-mono">
                    {errors.name.message}
                  </p>
                )}
              </div>
              <div className="space-y-1.5 sm:space-y-2">
                <Label
                  htmlFor="email"
                  className="text-[var(--primary)] text-sm sm:text-base font-mono"
                >
                  EMAIL_ADDRESS
                </Label>
                <Input
                  id="email"
                  type="email"
                  {...registerField("email")}
                  className="bg-[var(--bg-alt)] border-[var(--border-color)] text-[var(--text-main)] focus:border-[var(--primary)] h-11 sm:h-10 font-mono"
                />
                {errors.email && (
                  <p className="text-[var(--error)] text-xs font-mono">
                    {errors.email.message}
                  </p>
                )}
              </div>
              <div className="space-y-1.5 sm:space-y-2">
                <Label
                  htmlFor="githubUsername"
                  className="text-[var(--primary)] text-sm sm:text-base font-mono"
                >
                  GITHUB_USER
                </Label>
                <Input
                  id="githubUsername"
                  type="text"
                  {...registerField("githubUsername")}
                  className="bg-[var(--bg-alt)] border-[var(--border-color)] text-[var(--text-main)] focus:border-[var(--primary)] h-11 sm:h-10 font-mono"
                />
                {errors.githubUsername && (
                  <p className="text-[var(--error)] text-xs font-mono">
                    {errors.githubUsername.message}
                  </p>
                )}
              </div>
              <Button
                type="submit"
                className="w-full cyber-button h-12 sm:h-12 text-sm sm:text-base"
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : null}
                {isLoading ? "CREATING_ACCOUNT..." : "CREATE_ACCOUNT"}
              </Button>
            </form>
          )}

          <div className="text-center pt-2">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-xs sm:text-sm text-[var(--text-subtle)] hover:text-[var(--primary)] transition-colors font-mono"
            >
              {isLogin ? "NO_CREDENTIALS? REGISTER" : "HAS_CREDENTIALS? LOGIN"}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
